import * as txml from "txml";
import React, { useState, useEffect } from "react";
import Markdown from "../common/markdown-assistant";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Typography, IconButton } from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import { Chip } from "@mhome/ui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import DeleteIcon from "@mui/icons-material/Delete";
import { useHistory } from "react-router-dom";
import {
  batchGetAutomationNames,
  deleteAutomation,
} from "../service/automation-service";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { selectedMentionState, wsReadyState } from "../global-state";
import { useWebSocket } from "../ws/websocket";
import { checkScript } from "../service/assistant-service";
import { useToast } from "../common/toast";

// React component for automation card
const AutomationCard = ({
  name,
  linkParams,
  cardType = "automation",
  onAssistantInteraction,
}) => {
  const history = useHistory();
  const [automationName, setAutomationName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const { sendAndWaitWs } = useWebSocket();
  const wsReady = useRecoilValue(wsReadyState);
  const setSelectedMention = useSetRecoilState(selectedMentionState);
  const { successToast, errorToast } = useToast();

  useEffect(() => {
    const fetchAutomationInfo = async () => {
      if (!linkParams) {
        setLoading(false);
        return;
      }

      try {
        // Get automationId from linkParams object
        const automationId = linkParams.automationId;

        if (automationId) {
          const names = await batchGetAutomationNames(sendAndWaitWs, [
            automationId,
          ]);

          if (names?.automations?.[automationId]) {
            setAutomationName(names.automations[automationId]);
            setFetchError(false);
          } else {
            setFetchError(true);
          }
        }
      } catch (error) {
        console.error("Failed to fetch automation info:", error);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    if (wsReady) {
      fetchAutomationInfo();
    }
  }, [wsReady]);

  const handleMentionClick = (event) => {
    event.stopPropagation(); // Prevent card click
    console.debug("handleMentionClick", linkParams);

    // Get automationId from linkParams object
    const automationId = linkParams.automationId;

    if (automationId && automationName) {
      // Create automation mention data similar to entity mentions
      const mentionData = {
        id: automationId,
        type: "automation",
        title: automationName || name,
        subtitle: "Automation",
        typeName: "automation",
      };

      // Set the selected automation mention in Recoil state
      setSelectedMention(mentionData);
    }
  };

  const handleGotoClick = async (event) => {
    event.stopPropagation(); // Prevent card click
    console.debug("handleGotoClick", linkParams);

    // Get automationId and scriptId from linkParams object
    const automationId = linkParams.automationId;
    const scriptId = linkParams.scriptId;

    if (automationId) {
      // Build URL with hash if scriptId exists
      let url = `/automation/overview?id=${automationId}`;
      if (scriptId) {
        url += `#draft=${scriptId}`;
      }

      history.push(url);
    } else if (scriptId) {
      try {
        const scriptInfo = await checkScript(sendAndWaitWs, scriptId);

        if (scriptInfo && scriptInfo.automationId) {
          const url = `/automation/overview?id=${scriptInfo.automationId}#draft=${scriptId}`;
          history.push(url);
        } else {
          const url = `/automation/overview?draftId=${scriptId}`;
          history.push(url);
        }
      } catch (error) {
        console.error("Failed to fetch script info:", error);
        const url = `/automation/overview?draftId=${scriptId}`;
        history.push(url);
      }
    }

    // Close drawer on small screens when navigating
    if (onAssistantInteraction) {
      onAssistantInteraction();
    }
  };

  const handleDeleteAutomation = async (event) => {
    event.stopPropagation(); // Prevent card click
    console.debug("handleDeleteAutomation", linkParams);

    // Get automationId from linkParams object
    const automationId = linkParams.automationId;

    if (automationId) {
      const success = await deleteAutomation(
        sendAndWaitWs,
        automationId,
        true // force delete for quick commands
      );
      if (success) {
        successToast("Deleted.");
      } else {
        errorToast("Error!");
      }
    }
  };

  // Determine if this is a quick command card and get dot color
  const isQuickCommand = cardType === "mqc" || cardType === "sqc";

  // Use fetched name if available, otherwise use provided name or "Not found" for failed fetch
  const displayName = automationName || (fetchError ? "Not found" : name);

  const getDotColor = () => {
    if (cardType === "mqc") {
      return "hsl(var(--green-500))";
    } else if (cardType === "sqc") {
      return "hsl(var(--yellow-500))";
    }
    return null;
  };

  // If there's a fetch error, show a special "not found" card
  if (fetchError && !loading) {
    return (
      <Card
        className="my-2 border shadow-none bg-transparent"
        style={{
          borderColor: "hsl(var(--border))",
        }}
      >
        <CardContent className="p-2">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {!isQuickCommand && (
                <Chip
                  label="automation"
                  size="small"
                  style={{
                    backgroundColor: "hsl(var(--primary) / 0.1)",
                    color: "hsl(var(--primary))",
                    fontSize: "0.75rem",
                    height: "20px",
                  }}
                />
              )}
              {isQuickCommand && (
                <Chip
                  label={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: getDotColor(),
                          flexShrink: 0,
                        }}
                      />
                      quick command
                    </div>
                  }
                  size="small"
                  style={{
                    backgroundColor: "hsl(var(--secondary))",
                    color: "hsl(var(--secondary-foreground))",
                    fontSize: "0.75rem",
                    height: "20px",
                  }}
                />
              )}
            </div>
          </div>
          <Typography
            variant="subtitle2"
            style={{
              fontWeight: 600,
              color: "hsl(var(--muted-foreground))",
              marginTop: "8px",
            }}
          >
            Not found
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="my-2 border shadow-none bg-transparent transition-colors"
      style={{
        borderColor: "hsl(var(--border))",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "hsl(var(--primary))";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "hsl(var(--border))";
      }}
    >
      <CardContent className="p-2">
        {/* Top row: Chips on left, Mention and Arrow on right */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {!isQuickCommand && (
              <Chip
                label="automation"
                size="small"
                style={{
                  backgroundColor: "hsl(var(--primary) / 0.1)",
                  color: "hsl(var(--primary))",
                  fontSize: "0.75rem",
                  height: "20px",
                }}
              />
            )}
            {isQuickCommand && (
              <Chip
                label={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: getDotColor(),
                        flexShrink: 0,
                      }}
                    />
                    quick command
                  </div>
                }
                size="small"
                style={{
                  backgroundColor: "hsl(var(--secondary))",
                  color: "hsl(var(--secondary-foreground))",
                  fontSize: "0.75rem",
                  height: "20px",
                }}
              />
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {isQuickCommand && (
              <IconButton
                size="small"
                onClick={handleDeleteAutomation}
                style={{ color: "hsl(var(--muted-foreground))" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "hsl(var(--destructive))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "hsl(var(--muted-foreground))";
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleMentionClick}
              style={{ color: "hsl(var(--muted-foreground))" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "hsl(var(--primary))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "hsl(var(--muted-foreground))";
              }}
            >
              <AlternateEmailIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleGotoClick}
              style={{ color: "hsl(var(--muted-foreground))" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "hsl(var(--primary))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "hsl(var(--muted-foreground))";
              }}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {/* Title row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          {loading && (
            <CircularProgress
              size={16}
              style={{ color: "hsl(var(--primary))" }}
            />
          )}
          <Typography
            variant="subtitle2"
            style={{
              fontWeight: 600,
              color: "hsl(var(--foreground))",
            }}
          >
            {displayName}
          </Typography>
        </div>
      </CardContent>
    </Card>
  );
};

// Function to extract automation IDs from automation, mqc, and sqc tags
export const extractAutomationIds = (content) => {
  if (!content || typeof content !== "string") {
    return [];
  }
  const ids = new Set();
  try {
    // Match automation, automation_mqc, and automation_sqc tags
    // The regex uses a backreference (\1) to ensure the closing tag matches the opening part.
    const automationTagMatches = content.match(
      /<automation(|_mqc|_sqc)[^>]*>.*?<\/automation\1>/g
    );

    if (automationTagMatches) {
      automationTagMatches.forEach((tagString) => {
        try {
          const parsed = txml.parse(tagString, {
            keepWhitespace: false,
            noChildNodes: [],
          });
          if (parsed && parsed.length > 0) {
            const element = parsed[0];
            const tagName = element.tagName;

            if (tagName === "automation") {
              const automationId = element.attributes?.automationId;
              if (automationId && automationId.trim() !== "") {
                ids.add(automationId.trim());
              }
            } else if (
              tagName === "automation_mqc" ||
              tagName === "automation_sqc"
            ) {
              const idsAttr = element.attributes?.ids;
              if (idsAttr && idsAttr.trim() !== "") {
                const idList = idsAttr
                  .split(",")
                  .map((id) => id.trim())
                  .filter((id) => id);
                idList.forEach((id) => ids.add(id));
              }
            }
          }
        } catch (e) {
          console.warn(
            "Failed to parse automation tag for ID extraction:",
            tagString,
            e
          );
        }
      });
    }
  } catch (error) {
    console.warn("Error extracting automation IDs:", error);
  }
  return [...ids];
};

// Function to process automation XML tags and convert to markdown links
export const processTags = (content, onAssistantInteraction) => {
  if (!content || typeof content !== "string") {
    return { content, components: [] };
  }

  // Use txml to parse XML tags properly
  let processedContent = content;
  const reactComponents = []; // To store React components

  try {
    // Find all no_install tags and convert to markdown table
    const noInstallTagMatches = content.match(/<no_install>.*?<\/no_install>/g);

    if (noInstallTagMatches) {
      noInstallTagMatches.forEach((tagString) => {
        try {
          // Extract JSON content between the tags
          const jsonContent = tagString.replace(/<\/?no_install>/g, "");
          const integrations = JSON.parse(jsonContent);

          // Create markdown table with name and link columns
          let markdownTable = "\n| Integration Name | Link |\n";
          markdownTable += "|------------------|------|\n";

          integrations.forEach((integration) => {
            const name = integration.integrationName || "N/A";
            const id = integration.integrationId || "";
            const link = id ? `[Setup](/integration?id=${id})` : "N/A";
            markdownTable += `| ${name} | ${link} |\n`;
          });

          markdownTable += "\n";

          // Replace the tag with the markdown table
          processedContent = processedContent.replace(tagString, markdownTable);
        } catch (parseError) {
          console.warn(
            "Failed to parse no_install tag:",
            tagString,
            parseError
          );
          // If parsing fails, just remove the tags but keep the content
          processedContent = processedContent.replace(
            tagString,
            tagString.replace(/<\/?no_install>/g, "")
          );
        }
      });
    }

    // Find all automation tags using a simple regex to get the full tag strings
    const automationTagMatches = content.match(
      /<automation[^>]*>.*?<\/automation>/g
    );

    if (automationTagMatches) {
      automationTagMatches.forEach((tagString) => {
        try {
          // Parse the individual automation tag using txml
          const parsed = txml.parse(tagString, {
            keepWhitespace: false,
            noChildNodes: [],
          });

          if (parsed && parsed.length > 0) {
            const automationElement = parsed[0];

            if (automationElement.tagName === "automation") {
              const automationId = automationElement.attributes?.automationId;
              const scriptId = automationElement.attributes?.scriptId;

              // Use the content inside the tag as the fallback name
              const fallbackName =
                automationElement.children?.join("") || "Automation";

              // Create linkParams object
              const linkParams = {};
              if (automationId && automationId.trim() !== "") {
                linkParams.automationId = automationId;
              }
              if (scriptId && scriptId.trim() !== "") {
                linkParams.scriptId = scriptId;
              }

              // If we have parameters, create the component, otherwise just return the inner text
              let replacement;
              if (Object.keys(linkParams).length > 0) {
                const componentId = `automation-card-${reactComponents.length}`;
                const placeholder = `__REACT_COMPONENT_${componentId}__`;
                reactComponents.push({
                  id: componentId,
                  component: (
                    <AutomationCard
                      key={componentId}
                      name={fallbackName}
                      linkParams={linkParams}
                      onAssistantInteraction={onAssistantInteraction}
                    />
                  ),
                });
                replacement = placeholder;
              } else {
                replacement = fallbackName;
              }

              // Replace the original tag with the placeholder
              processedContent = processedContent.replace(
                tagString,
                replacement
              );
            }
          }
        } catch (parseError) {
          // If parsing fails for this specific tag, just leave it as is
          console.warn(
            "Failed to parse automation tag:",
            tagString,
            parseError
          );
        }
      });
    }

    // Find all automation_mqc and automation_sqc tags and convert to AutomationCard components
    const automationMqcSqcTagMatches = content.match(
      /<automation_(mqc|sqc)[^>]*>.*?<\/automation_(mqc|sqc)>/g
    );

    if (automationMqcSqcTagMatches) {
      automationMqcSqcTagMatches.forEach((tagString) => {
        try {
          // Parse the individual automation_mqc/automation_sqc tag using txml
          const parsed = txml.parse(tagString, {
            keepWhitespace: false,
            noChildNodes: [],
          });

          if (parsed && parsed.length > 0) {
            const automationElement = parsed[0];
            const tagName = automationElement.tagName;

            if (tagName === "automation_mqc" || tagName === "automation_sqc") {
              const ids = automationElement.attributes?.ids;

              if (ids && ids.trim() !== "") {
                // Split comma-separated IDs and create AutomationCard components
                const idList = ids
                  .split(",")
                  .map((id) => id.trim())
                  .filter((id) => id);

                // Extract card type from tag name (mqc or sqc)
                const cardType = tagName === "automation_mqc" ? "mqc" : "sqc";

                let replacement = "";
                idList.forEach((id) => {
                  const componentId = `automation-card-${reactComponents.length}`;
                  const placeholder = `__REACT_COMPONENT_${componentId}__`;
                  const linkParams = { automationId: id };

                  reactComponents.push({
                    id: componentId,
                    component: (
                      <AutomationCard
                        key={componentId}
                        name="Automation"
                        linkParams={linkParams}
                        cardType={cardType}
                        onAssistantInteraction={onAssistantInteraction}
                      />
                    ),
                  });

                  replacement += placeholder;
                });

                // Replace the tag with the placeholders
                processedContent = processedContent.replace(
                  tagString,
                  replacement
                );
              } else {
                // If no ids attribute, just remove the tags
                processedContent = processedContent.replace(
                  tagString,
                  automationElement.children?.join("") || ""
                );
              }
            }
          }
        } catch (parseError) {
          console.warn(
            "Failed to parse automation_mqc/automation_sqc tag:",
            tagString,
            parseError
          );
          // If parsing fails, just remove the tags but keep the content
          const innerContent = tagString.replace(
            /<\/?automation_(mqc|sqc)[^>]*>/g,
            ""
          );
          processedContent = processedContent.replace(tagString, innerContent);
        }
      });
    }
  } catch (error) {
    // If there's any error in the overall process, just return the original content
    console.warn("Error processing automation tags:", error);
    return { content, components: [] };
  }

  // Then, escape any remaining XML tags for display as raw text
  // This will convert < and > to HTML entities so they display literally in markdown
  processedContent = processedContent
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return { content: processedContent, components: reactComponents };
};

// Helper component to render processed content with React components
export const ProcessedContent = ({
  content,
  components,
  markdownComponents,
}) => {
  if (!components || components.length === 0) {
    return <Markdown components={markdownComponents}>{content}</Markdown>;
  }

  // Split content by React component placeholders
  let parts = [content];
  components.forEach(({ id }) => {
    const placeholder = `__REACT_COMPONENT_${id}__`;
    const newParts = [];
    parts.forEach((part) => {
      if (typeof part === "string") {
        const splitParts = part.split(placeholder);
        if (splitParts.length > 1) {
          newParts.push(splitParts[0]);
          newParts.push({ type: "component", id });
          newParts.push(splitParts.slice(1).join(placeholder));
        } else {
          newParts.push(part);
        }
      } else {
        newParts.push(part);
      }
    });
    parts = newParts;
  });

  // Filter out empty strings
  parts = parts.filter((part) => part !== "");

  // Create a map of components by ID for easy lookup
  const componentMap = {};
  components.forEach(({ id, component }) => {
    componentMap[id] = component;
  });

  return (
    <div>
      {parts.map((part, index) => {
        if (typeof part === "string") {
          // Only render markdown if the string is not empty
          if (part.trim()) {
            // Add line breaks around the markdown content to ensure proper block-level rendering
            const trimmedPart = part.trim();
            const needsLineBreakBefore =
              index > 0 && parts[index - 1]?.type === "component";
            const needsLineBreakAfter =
              index < parts.length - 1 &&
              parts[index + 1]?.type === "component";

            let contentToRender = trimmedPart;
            if (needsLineBreakBefore) {
              contentToRender = "\n\n" + contentToRender;
            }
            if (needsLineBreakAfter) {
              contentToRender = contentToRender + "\n\n";
            }

            return (
              <Markdown key={index} components={markdownComponents}>
                {contentToRender}
              </Markdown>
            );
          }
          return null;
        } else if (part.type === "component") {
          // Render React components as separate block-level elements
          return <div key={index}>{componentMap[part.id] || null}</div>;
        }
        return null;
      })}
    </div>
  );
};
